import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Divider,
  Grid,
  IconButton,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAllProfileGroups } from 'src/shared/services/profileGroupService';
import { ProfileGroup } from 'src/modules/member/models/profileGroup';
import { toast } from 'react-toastify';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { createFormSettingField, updateFormSettingField, FormSettingField } from 'src/shared/services/memberFormSettingsService';

type MemberFormSettingEditModalProps = {
  formName: string;
  field: FormSettingField | null;
  usedFieldNames: string[];
  onSave: () => void;
  onClose: () => void;
};

const fieldTypes = [
  { value: 1, label: 'Text' },
  { value: 2, label: 'Flerrad text' },
  { value: 3, label: 'Textblock' },
  { value: 4, label: 'Dropdown' },
  { value: 7, label: 'Kryssruta' },
  { value: 10, label: 'Landsväljare' },
  { value: 12, label: 'Kommun' },
  { value: 13, label: 'Län' },
  { value: 14, label: 'Postnr' },
  { value: 15, label: 'Datum' },
  { value: 21, label: 'Etikett' },
  { value: 33, label: 'Rubrik' },
];

const profileFieldTypes = [
  { value: 0, label: 'Kryssrutor (multi)' },
  { value: 1, label: 'Kryssrutor (single)' },
  { value: 4, label: 'Dropdown' },
];

const isProfileField = (name: string) => name.startsWith('profileSpecial') || name === 'profile' || name === 'profileHead';

const lockedTextFields = new Set([
  'firstName',
  'lastName',
  'birthDate',
  'postAddress',
  'address',
  'addressOther',
  'COAddress',
  'email',
  'phone',
  'phoneOther',
  'memberNr',
  'postNr',
  'comment',
  'custom1',
  'custom2',
  'custom3',
  'custom4',
  'custom5',
  'custom6',
  'custom7',
  'custom8',
  'custom9',
  'custom10',
]);
const lockedMultilineFields = new Set(['note']);
const lockedDateFields = new Set(['admDate', 'payedDate', 'expireDate', 'resDate']);
const lockedDropdownFields = new Set(['resCause']);
const lockedAssociationFields = new Set(['association']);
const lockedCheckboxFields = new Set(['newsPaper', 'newsPaper2', 'newsPaper3', 'newsPaper4', 'newsPaper5']);
const lockedCountyFields = new Set(['countyCode']);
const lockedMunicipalityFields = new Set(['municipalityCode']);
const lockedCountryFields = new Set(['country']);
const isHeadlineField = (name: string) => name.startsWith('headline');
const isTextField = (name: string) => name.startsWith('text');
const isFieldTypeLocked = (name: string) =>
  lockedTextFields.has(name) ||
  lockedMultilineFields.has(name) ||
  lockedDateFields.has(name) ||
  lockedDropdownFields.has(name) ||
  lockedCheckboxFields.has(name) ||
  lockedCountyFields.has(name) ||
  lockedMunicipalityFields.has(name) ||
  lockedCountryFields.has(name) ||
  lockedAssociationFields.has(name) ||
  isHeadlineField(name) ||
  isTextField(name);

const validators = [
  { value: '', label: 'Ingen' },
  { value: 'validateEmail', label: 'E-post' },
  { value: 'validatePhone', label: 'Telefonnummer' },
  { value: 'validatePostNr', label: 'Postnummer' },
  { value: 'validateCivicRegNr13', label: 'Personnummer (13 siffror)' },
  { value: 'validateOrgNr11', label: 'Organisationsnummer (11 siffror)' },
  { value: 'validateBirthdate', label: 'Födelsedatum' },
  { value: 'validateTxt', label: 'Text' },
];

interface DropdownOption {
  id: string;
  label: string;
  value: string;
}

const parseDropdownOptions = (extra: string): { placeholder: string; options: DropdownOption[] } => {
  if (!extra || !extra.startsWith('|')) return { placeholder: '', options: [] };
  const parts = extra.split('|').filter(Boolean);
  if (parts.length === 0) return { placeholder: '', options: [] };
  const placeholder = parts[0];
  const options: DropdownOption[] = [];
  for (let i = 1; i < parts.length; i += 2) {
    options.push({ id: crypto.randomUUID(), value: parts[i], label: parts[i + 1] || parts[i] });
  }
  return { placeholder, options };
};

const serializeOptions = (placeholder: string, options: DropdownOption[]): string => {
  const parts = ['', placeholder];
  options.forEach((opt) => {
    parts.push(opt.value || opt.label);
    parts.push(opt.label);
  });
  return parts.join('|');
};

const customFieldNames = Array.from({ length: 10 }, (_, i) => `custom${i + 1}`);

const getDisplayType = (name: string, currentType: number): number => {
  if (lockedTextFields.has(name)) return 1;
  if (lockedMultilineFields.has(name)) return 2;
  if (lockedDateFields.has(name)) return 15;
  if (lockedDropdownFields.has(name)) return 4;
  if (lockedCheckboxFields.has(name)) return 7;
  if (lockedCountyFields.has(name)) return 13;
  if (lockedMunicipalityFields.has(name)) return 12;
  if (lockedCountryFields.has(name)) return 10;
  if (lockedAssociationFields.has(name)) return 4;
  if (isHeadlineField(name)) return 33;
  if (isTextField(name)) return 3;
  return currentType;
};

export const MemberFormSettingEditModal = ({ formName, field, usedFieldNames, onSave, onClose }: MemberFormSettingEditModalProps) => {
  const { t } = useTranslation(['settings', 'common']);
  const { currentUser } = useGlobals();
  const assSelectorType = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.AssSelectorType, 'local');
  const localLabel = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.LocalLabel, 'Förening');
  const countyLabel = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.CountyLabel, 'Distrikt');
  const isEdit = !!field;

  const [fieldName, setFieldName] = useState(field?.fieldName || '');
  const [heading, setHeading] = useState(field?.heading || '');
  const [type, setType] = useState(field?.type ?? 1);
  const [profileGroups, setProfileGroups] = useState<ProfileGroup[]>([]);

  // Parsa profilgrupp-id från extra (t.ex. "PG123" → "123")
  const parseProfileGroupId = (extra: string) => (extra?.startsWith('PG') ? extra.substring(2) : '');
  const [selectedProfileGroupId, setSelectedProfileGroupId] = useState(parseProfileGroupId(field?.extra || ''));

  const fetchProfileGroups = useCallback(async () => {
    if (!isProfileField(fieldName)) return;
    try {
      const res = await getAllProfileGroups(false, true);
      setProfileGroups(res.data.results || []);
    } catch (e) {
      ConsoleHelper.log(e);
    }
  }, [fieldName]);

  useEffect(() => {
    fetchProfileGroups();
  }, [fetchProfileGroups]);
  const [show, setShow] = useState(field?.show ?? true);
  const [required, setRequired] = useState(field?.required ?? false);
  const [locked, setLocked] = useState(field?.locked ?? false);
  const [extra, setExtra] = useState(field?.extra || '');
  const [validator, setValidator] = useState(field?.validator || '');

  // Dropdown-options (för typ 4 dropdown, ej profiler)
  const parsedOptions = field && !isProfileField(field.fieldName) ? parseDropdownOptions(field.extra || '') : { placeholder: '', options: [] };
  const [dropdownPlaceholder, setDropdownPlaceholder] = useState(parsedOptions.placeholder);
  const [dropdownOptions, setDropdownOptions] = useState<DropdownOption[]>(parsedOptions.options);

  const handleAddOption = () => setDropdownOptions([...dropdownOptions, { id: crypto.randomUUID(), label: '', value: '' }]);
  const handleRemoveOption = (index: number) => setDropdownOptions(dropdownOptions.filter((_, i) => i !== index));
  const [errorMessage, setErrorMessage] = useState(field?.errorMessage || '');
  const [fieldSize, setFieldSize] = useState(field?.fieldSize ?? 1);
  const [row, setRow] = useState(field?.row ?? 99);
  const [loading, setLoading] = useState(false);

  const availableFieldNames = customFieldNames.filter((n) => !usedFieldNames.includes(n) || n === field?.fieldName);

  const handleSave = async () => {
    if (!heading.trim()) return;
    if (!isEdit && !fieldName) return;

    setLoading(true);
    try {
      let effectiveExtra = extra;
      if (isProfileField(fieldName) && selectedProfileGroupId) {
        effectiveExtra = `PG${selectedProfileGroupId}`;
      } else if (type === 4 && !isProfileField(fieldName)) {
        effectiveExtra = serializeOptions(dropdownPlaceholder || heading, dropdownOptions);
      }

      const data = {
        fieldName: isEdit ? field.fieldName : fieldName,
        heading,
        show,
        required,
        locked,
        // Spara den typ som visas i (den låsta) dropdownen – annars kan ett custom-fält med type 0 i DB
        // skrivas tillbaka som 0 trots att UI visar "Text", och då renderas det inte i formuläret.
        type: getDisplayType(isEdit ? field.fieldName : fieldName, type),
        extra: effectiveExtra,
        validator,
        errorMessage,
        fieldSize,
        row,
        col: 0,
      };

      if (isEdit) {
        await updateFormSettingField(formName, field.formSettingsID, data);
        toast.success(t('settings:memberRegistry.saveSuccess') as string);
      } else {
        await createFormSettingField(formName, data);
        toast.success(t('settings:memberRegistry.createSuccess') as string);
      }
      onSave();
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(t('settings:memberRegistry.saveError') as string);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{isEdit ? t('settings:memberRegistry.editField') : t('settings:memberRegistry.addField')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Fältnamn + Rubrik */}
          <Grid item xs={6}>
            <TextField
              select={!isEdit}
              fullWidth
              size='small'
              label={t('settings:memberRegistry.fieldName')}
              value={fieldName}
              onChange={(e) => setFieldName(e.target.value)}
              disabled={isEdit}
            >
              {availableFieldNames.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {!lockedAssociationFields.has(fieldName) && (
            <Grid item xs={6}>
              <TextField
                fullWidth
                size='small'
                label={t('settings:memberRegistry.heading')}
                value={heading}
                onChange={(e) => setHeading(e.target.value)}
              />
            </Grid>
          )}

          {lockedAssociationFields.has(fieldName) && (
            <Grid item xs={12}>
              <Typography variant='body2' color='text.secondary'>
                {assSelectorType === 'link' || assSelectorType === 'opt'
                  ? t('settings:memberRegistry.associationFieldDescriptionBoth', { countyLabel, localLabel })
                  : t('settings:memberRegistry.associationFieldDescriptionLocal', { localLabel })}
              </Typography>
            </Grid>
          )}

          {/* Fälttyp */}
          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              size='small'
              label={t('settings:memberRegistry.fieldType')}
              value={getDisplayType(fieldName, type)}
              onChange={(e) => setType(Number(e.target.value))}
              disabled={isFieldTypeLocked(fieldName)}
            >
              {(isProfileField(fieldName) ? profileFieldTypes : fieldTypes).map((ft) => (
                <MenuItem key={ft.value} value={ft.value}>
                  {ft.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Profilgrupp (direkt under fälttyp) */}
          {isProfileField(fieldName) && (
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                size='small'
                label={t('settings:memberRegistry.profileGroup')}
                value={selectedProfileGroupId}
                onChange={(e) => setSelectedProfileGroupId(e.target.value)}
              >
                {profileGroups.map((pg) => (
                  <MenuItem key={pg.memberProfile_id} value={String(pg.memberProfile_id)}>
                    {pg.title}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {/* Dropdown-options (direkt under fälttyp, ej profiler) */}
          {!isProfileField(fieldName) && !lockedDropdownFields.has(fieldName) && type === 4 && (
            <Grid item xs={12}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
                {t('settings:memberRegistry.dropdownOptionsTitle')}
              </Typography>
              <TextField
                fullWidth
                size='small'
                label={t('settings:memberRegistry.placeholderText')}
                value={dropdownPlaceholder}
                onChange={(e) => setDropdownPlaceholder(e.target.value)}
                sx={{ mb: 1 }}
              />
              {dropdownOptions.map((opt, index) => (
                <Box key={opt.id} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    size='small'
                    label={`${t('settings:memberRegistry.optionValue')} ${index + 1}`}
                    value={opt.value}
                    onChange={(e) => {
                      const updated = [...dropdownOptions];
                      updated[index] = { ...updated[index], value: e.target.value };
                      setDropdownOptions(updated);
                    }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size='small'
                    label={`${t('settings:memberRegistry.optionText')} ${index + 1}`}
                    value={opt.label}
                    onChange={(e) => {
                      const updated = [...dropdownOptions];
                      updated[index] = { ...updated[index], label: e.target.value };
                      setDropdownOptions(updated);
                    }}
                    sx={{ flex: 1 }}
                  />
                  <IconButton size='small' color='error' onClick={() => handleRemoveOption(index)}>
                    <DeleteOutlineIcon fontSize='small' />
                  </IconButton>
                </Box>
              ))}
              <Button size='small' startIcon={<AddCircleOutlineIcon />} onClick={handleAddOption}>
                {t('settings:memberRegistry.addOption')}
              </Button>
            </Grid>
          )}

          {/* Maxlängd (text) */}
          {type === 1 &&
            !isProfileField(fieldName) &&
            !isHeadlineField(fieldName) &&
            !isTextField(fieldName) &&
            !lockedAssociationFields.has(fieldName) && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size='small'
                  label={t('settings:memberRegistry.maxLength')}
                  type='number'
                  value={extra || ''}
                  onChange={(e) => setExtra(e.target.value)}
                />
              </Grid>
            )}

          {/* Checkboxar */}
          <Grid item xs={12}>
            <FormControlLabel control={<Checkbox checked={show} onChange={(_, c) => setShow(c)} />} label={t('settings:memberRegistry.visible')} />
            {type !== 7 &&
              !lockedCheckboxFields.has(fieldName) &&
              !isHeadlineField(fieldName) &&
              !isTextField(fieldName) &&
              !lockedAssociationFields.has(fieldName) && (
                <>
                  <FormControlLabel
                    control={<Checkbox checked={required} onChange={(_, c) => setRequired(c)} />}
                    label={t('settings:memberRegistry.required')}
                  />
                  <FormControlLabel
                    control={<Checkbox checked={locked} onChange={(_, c) => setLocked(c)} />}
                    label={t('settings:memberRegistry.locked')}
                  />
                </>
              )}
          </Grid>

          {/* Validator */}
          {!isProfileField(fieldName) &&
            type !== 7 &&
            !lockedCheckboxFields.has(fieldName) &&
            !lockedCountyFields.has(fieldName) &&
            !lockedMunicipalityFields.has(fieldName) &&
            !lockedCountryFields.has(fieldName) &&
            !isHeadlineField(fieldName) &&
            !isTextField(fieldName) &&
            !lockedAssociationFields.has(fieldName) && (
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  size='small'
                  label={t('settings:memberRegistry.validator')}
                  value={validator}
                  onChange={(e) => setValidator(e.target.value)}
                >
                  {validators.map((v) => (
                    <MenuItem key={v.value} value={v.value}>
                      {v.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
          {/* Felmeddelande */}
          {!isProfileField(fieldName) &&
            type !== 7 &&
            !lockedCheckboxFields.has(fieldName) &&
            !isHeadlineField(fieldName) &&
            !isTextField(fieldName) &&
            !lockedAssociationFields.has(fieldName) && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size='small'
                  label={t('settings:memberRegistry.errorMessage')}
                  value={errorMessage}
                  onChange={(e) => setErrorMessage(e.target.value)}
                />
              </Grid>
            )}

          {/* Placering (längst ned) */}
          <Grid item xs={12}>
            <Divider sx={{ mb: 1 }} />
            <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
              {t('settings:memberRegistry.placement')}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size='small'
              label={t('settings:memberRegistry.row')}
              type='number'
              value={row}
              onChange={(e) => setRow(Number(e.target.value))}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              size='small'
              label={t('settings:memberRegistry.fieldWidth')}
              value={fieldSize}
              onChange={(e) => setFieldSize(Number(e.target.value))}
            >
              <MenuItem value={1}>25%</MenuItem>
              <MenuItem value={2}>50%</MenuItem>
              <MenuItem value={3}>75%</MenuItem>
              <MenuItem value={4}>100%</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='primary'>
          {t('common:button.cancel')}
        </Button>
        <Button onClick={handleSave} color='primary' variant='contained' disabled={loading || !heading.trim()}>
          {t('common:button.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
